import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {AppComponentBase} from '@shared/common/app-component-base';
import {
    BasketArticleDto,
    ICreateOrUpdateVirtualBankDto,
    ICreateVirtualBankUserInputDtoWithId,
    ILocalWarehouseAmountToChangeListInputDto, ILocalWarehouseArticleAmountToChangeDto,
    LocalWarehouseAmountToChangeListInputDto, LocalWarehouseArticleAmountToChangeDto,
    WebShopServiceProxy
} from '@shared/service-proxies/service-proxies';
import {ModalDirective} from 'ngx-bootstrap/modal';
import {debounceTime, distinctUntilChanged, finalize, takeUntil} from 'rxjs/operators';
import {FieldAgentService} from '@app/webshop/field-agent/shared/services/field-agent.service';
import {EditCustomerMode} from '@app/webshop/shared/models';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import _ from 'lodash';
import {LocalWarehouseDataRow, MLWService} from '@app/webshop/shared/services/mlw.service';
import {Subject, throwError} from '@node_modules/rxjs';
import {tap} from '@node_modules/rxjs/dist/types/operators';
import moment from 'moment';
import {Table} from '@node_modules/primeng/table';
import {
    ManageLocalWarehouseItemsComponent
} from '@app/webshop/field-agent/modals/local-warehouse/manage-local-warehouse-items.component';
import {UniversalCounterComponent} from '@app/webshop/shared/common/universal-counter/universal-counter.component';

@Component({
    selector: 'app-edit-customer-modal',
    templateUrl: './edit-local-warehouse-modal.component.html',
    styleUrls: ['./edit-local-warehouse-modal.component.less']
})
export class EditLocalWarehouseModalComponent extends AppComponentBase implements OnInit, OnDestroy {
    @ViewChild('editLocalWarehouseModal', {static: true}) modal: ModalDirective;

    @ViewChild('manageLocalWarehouseItems') manageLocalWarehouseItemsComponent: ManageLocalWarehouseItemsComponent;
    @ViewChild('universalCounter') universalCounterComponent: UniversalCounterComponent;

    active = false;
    saving = false;

    article: BasketArticleDto;
    warehouseForm!: FormGroup;
    newRowForm: FormGroup;

    isSelectingArticles = false;

    private mLWService: MLWService;

    get isAddMode(): boolean {
        return this.mLWService.isAddMode;
    }

    protected readonly destroy$ = new Subject<void>();

    doubleConfirmation = false;
    isApplying = false;
    isSaveAndClose = false;

    constructor(
        injector: Injector,
        private _webShopServiceProxy: WebShopServiceProxy,
        private fb: FormBuilder
    ) {
        super(injector);

        this.mLWService = injector.get(MLWService);
    }

    ngOnInit(): void {
        this._init();

        this.mLWService.selectedRow$
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe((data: LocalWarehouseDataRow | undefined) => {
                if (data) {
                    this.warehouseForm.get('currentAmount')?.setValue(data.amount);

                    if (this.isSelectingArticles) {
                        this.onChangeCounter(this.article.quantity);
                    } else {
                        this.onChangeCounter(1);
                    }

                    this.newRowForm.get('tht')?.setValue(data.tht);
                    this.newRowForm.get('batchNumber')?.setValue(data.batchNumber);
                } else {
                    this.newRowForm.reset();

                    this.warehouseForm.get('currentAmount')?.setValue(0);
                    this.onChangeCounter(1);
                }
            });
    }

    private _init() {
        this._initWarehouseForm();
        this._initNewRowForm();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private _isObjectEmptyOrNull(obj: any): boolean {
        return Object.values(obj).every(value => value === null || value === undefined);
    }

    private _initWarehouseForm() {
        this.warehouseForm = this.fb.group({
            title: [undefined, Validators.required],
            articleCode: [undefined, Validators.required],
            reason: [undefined, Validators.required],
            amountToChange: [1],
            currentAmount: [undefined],
            newAmount: [1]
        });

        this.warehouseForm.valueChanges
            .pipe(
                debounceTime(300), // Wait 300ms after last change
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)) // Ignore duplicate values
            )
            .subscribe(value => {
                this._resetDoubleConfirmationFlow();
            });
    }

    private _initNewRowForm() {
        this.newRowForm = this.fb.group({
            tht: [null],  // Date field
            batchNumber: [''],  // Text field
        });

        // Subscribe to `tht` field changes
        this.newRowForm.valueChanges
            .pipe(
                debounceTime(300), // Wait 300ms after last change
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)) // Ignore duplicate values
            )
            .subscribe(value => {
                if (!this._isObjectEmptyOrNull(value)) {
                    this.handleValueChange(value);
                }

                this._resetDoubleConfirmationFlow();
            });
    }

    private _resetDoubleConfirmationFlow() {
        this.isSaveAndClose = false;
        this.isApplying = false;
        this.doubleConfirmation = false;
    }

    handleValueChange(value: any) {
        if (!value) {
            return;
        }

        // // Normalize both values to YYYY-MM-DD format for comparison
         const formattedValue = moment(value.tht).format('YYYY-MM-DD');
         const item = this.mLWService.itemsToShowValue
             .find((item) => moment(item.tht).format('YYYY-MM-DD') === formattedValue
                 && item.batchNumber === value.batchNumber);

        if (item) {
            this.newRowForm.get('batchNumber')?.setValue(item.batchNumber);
            this.warehouseForm.get('currentAmount')?.setValue(item.amount);

            if (!this.isRowSelected) {
                this.onChangeCounter(1);
            }
        }
    }

    onChangeCounter(value) {
        if (value !== null && value !== undefined) {
            this.warehouseForm.get('amountToChange')?.setValue(value);
            // React to changes, e.g., update newAmount
            const currentAmount = this.warehouseForm.get('currentAmount')?.value || 0;
            const result = currentAmount + value;
            this.warehouseForm.get('newAmount')?.setValue(result < 0 ? 0 : result);
        }
    }

    private _setFormData(input: BasketArticleDto) {
        this.warehouseForm.get('currentAmount')?.setValue(0);
        this.warehouseForm.get('articleCode')?.setValue(input.code);
        this.warehouseForm.get('title')?.setValue(input.title);
        this.warehouseForm.get('newAmount')?.setValue(0);
    }

    show(input: BasketArticleDto, isSelectingArticles: boolean = false): ModalDirective {
        this.active = true;

        if (input) {
            this.article = _.cloneDeep(input);
            this._setFormData(this.article);
        }

        this.isSelectingArticles = isSelectingArticles;
        if (this.isSelectingArticles) {
            this.warehouseForm.get('reason')?.setValue(`Promotion of ${moment().utc().format('DD-MM-YYYY')}`);
            this.warehouseForm.get('amountToChange')?.setValue(this.article.quantity);
        }

        this.modal.show();
        return this.modal;
    }

    onShown(): void {
        //document.getElementById('ProjectName').focus();
    }

    close(): void {
        this.active = false;
        this.warehouseForm.reset();
        this.newRowForm.reset();

        this.mLWService.disableAddMode();
        this._resetDoubleConfirmationFlow();

        this.resetQuantity(this.article);
        this.doubleConfirmation = false;
        this.article = undefined;

        this.modal.hide();
    }

    onSave(): void {
        if (!this.doubleConfirmation || this.isApplying) {
            if (this.isApplying) {
                this.isApplying = false;
            }
            this.doubleConfirmation = true;
            this.isSaveAndClose = true;
            return;
        }
        this.saving = true;

        if (this.isSelectingArticles) {
            this._addItemToBasket();
            this.close();
            this.saving = false;
        } else {
            const formData = this.warehouseForm.value;

            const data = {
                articleCode: formData.articleCode,
                amountToChange: formData.amountToChange,
                newAmount: !this._isNewFormEmpty() ? formData.newAmount : undefined,
                reason: formData.reason,
                extendedProperties: this.mLWService.convertToArticleExtendedPropertyDto(this.newRowForm.value)
            } as ILocalWarehouseArticleAmountToChangeDto;

            const input = {
                articlesToChange: [new LocalWarehouseArticleAmountToChangeDto(data)]
            } as ILocalWarehouseAmountToChangeListInputDto;

            this.mLWService.updateLocalWarehouseAmount$(input)
                .pipe(
                    finalize(() => this.saving = false)
                )
                .subscribe((data) => {
                    this.close();

                    abp.event.trigger('app.combineView.refresh');
                });
        }
    }

    goBack() {
        this.mLWService.disableAddMode();
        this.newRowForm.reset();

        this._resetDoubleConfirmationFlow();

        this.warehouseForm.get('amountToChange')?.setValue(1);
        this.warehouseForm.get('currentAmount')?.setValue(0);
        this.warehouseForm.get('newAmount')?.setValue(1);
        this.warehouseForm.get('reason')?.setValue(undefined);

        this.article.quantity = 1;

        this.warehouseForm.markAsPristine();  // Removes the "dirty" state
        this.warehouseForm.markAsUntouched(); // Removes the "touched" state
        this.newRowForm.markAsPristine();  // Removes the "dirty" state
        this.newRowForm.markAsUntouched(); // Removes the "touched" state
    }

    protected _addItemToBasket() {
        this.mLWService.updateBasketArticleDto(this.article, this.newRowForm.value);
        this.webshopService.addItem(this.article);
        this.resetQuantity(this.article);
    }

    onApply() {
        if (!this.doubleConfirmation || this.isSaveAndClose) {
            if (this.isSaveAndClose) {
                this.isSaveAndClose = false;
            }
            this.doubleConfirmation = true;
            this.isApplying = true;
            return;
        }

        if (this.isSelectingArticles) {
            this._addItemToBasket();
            this._reset();
        } else {
            const formData = this.warehouseForm.value;
            const data = {
                articleCode: formData.articleCode,
                amountToChange: formData.amountToChange,
                newAmount: !this._isNewFormEmpty() ? formData.newAmount : undefined,
                reason: formData.reason,
                extendedProperties: this.mLWService.convertToArticleExtendedPropertyDto(this.newRowForm.value)
            } as ILocalWarehouseArticleAmountToChangeDto;

            const input = {
                articlesToChange: [new LocalWarehouseArticleAmountToChangeDto(data)]
            } as ILocalWarehouseAmountToChangeListInputDto;

            this._webShopServiceProxy
                .updateLocalWarehouseAmount(new LocalWarehouseAmountToChangeListInputDto(input))
                .pipe(
                    finalize(() => this.saving = false)
                )
                .subscribe((data) => {
                    this._reset();
                    abp.event.trigger('app.combineView.refresh');
                });
        }
    }

    private _reset() {
        this.article.quantity = 1;
        if (this.isRowSelected) {
            this.mLWService.onRowUnselect();
            if (this.manageLocalWarehouseItemsComponent) {
                this.manageLocalWarehouseItemsComponent.selectedProduct = undefined;
            }

            this.warehouseForm.get('amountToChange')?.setValue(1);
            this.warehouseForm.get('currentAmount')?.setValue(0);
            this.warehouseForm.get('newAmount')?.setValue(1);

            this.newRowForm.reset();
        } else {
            this._updateCounters();
        }

        //this.mLWService.updateLocalWarehouse(this.article.code);
        this.doubleConfirmation = false;
    }

    get isRowSelected(): boolean {
        return !!this.mLWService.selectedRowValue;
    }

    get isApplyDisabled(): boolean {
        if (this.isRowSelected) {
            return this.warehouseForm.invalid || this.newRowForm.invalid;
        }

        return this.newRowForm.invalid || !this.isRowSelected;
    }

    private _updateCounters() {
        if (this._isNewFormEmpty()) {
            this.warehouseForm.get('amountToChange')?.setValue(1);
            this.warehouseForm.get('currentAmount')?.setValue(0);
            this.warehouseForm.get('newAmount')?.setValue(1);
            this.warehouseForm.get('reason')?.setValue('');
            return;
        }

        const newAmount = this.warehouseForm.get('newAmount')?.value;
        this.warehouseForm.get('currentAmount')?.setValue(newAmount);
        this.onChangeCounter(this.warehouseForm.get('amountToChange')?.value);
    }

    private _isNewFormEmpty(): boolean {
        const newForm = this.newRowForm.value;
        return !newForm.tht && !newForm.batchNumber;
    }

    onRowToggle($event) {
        this.warehouseForm.get('amountToChange')?.setValue(1);
        this.resetQuantity(this.article);
    }

    onAddNew($event) {
        this.warehouseForm.get('amountToChange')?.setValue(1);
        this.warehouseForm.get('currentAmount')?.setValue(0);
        this.warehouseForm.get('newAmount')?.setValue(1);

        this.newRowForm.reset();

        this.resetQuantity(this.article);
    }

}
